import { useMemo, useState } from 'react';
import { ControllerRenderProps, useController, useFormContext } from 'react-hook-form';
import { Alert, Collapse, Stack, Typography } from '@mui/material';
import GppGoodIcon from '@mui/icons-material/GppGood';
import { RendererType } from '../../../../../data-models/field-definition';
import { ManageCompanyFieldsTypes } from '../../../../../schemas/template-assignment-schema';
import { RespondentStatusItem } from '../../../../../schemas/RespondentApprovalStatus.schema';
import {
  editableChips,
  mergeRespondentDisplay,
  reconcileSuggestion,
} from '../../../../../util/respondentStatus';
import { createFormField } from '../../../../../view-models/form.view-model';
import { FieldFreeLabels } from '../../../../../components/Form/Display/FieldFreeLabels';
import { FormField, Label } from '../../../../../components/Form/FormComponents';
import { InvalidEmailsErrorMessage } from '../components/KPIAssignRequestModal/emailArrayValidator';

interface PortcoRespondentsFieldProps {
  companyStatuses: ReadonlyMap<string, RespondentStatusItem> | undefined;
}

export function PortcoRespondentsField({ companyStatuses }: PortcoRespondentsFieldProps) {
  const { control } = useFormContext<ManageCompanyFieldsTypes>();
  const { field } = useController({ name: 'respondents', control });
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const respondents = useMemo(() => (field.value ?? []) as string[], [field.value]);
  const { approved, pending } = useMemo(
    () => mergeRespondentDisplay(respondents, companyStatuses),
    [respondents, companyStatuses]
  );
  // Working set is state (not derived): a session-added email isn't in SB `pending` until it syncs.
  const [workingChips, setWorkingChips] = useState<string[]>(pending);
  // But filter against live status each render, so a chip the company has since approved/rejected drops
  // out of the editable set (no stale/duplicate chip; an approved respondent can't be removed here).
  const chips = useMemo(() => editableChips(workingChips, companyStatuses), [workingChips, companyStatuses]);

  const suggestField = useMemo(
    () =>
      createFormField({
        key: 'suggestRespondents',
        renderer: RendererType.freeLabels,
        placeholder: 'Add an email and press Enter',
        rendererMeta: { multi: true, values: [], maxTags: -1 },
      }),
    []
  );

  const handleSuggestChange = (next: string[]): void => {
    const result = reconcileSuggestion(chips, next, companyStatuses, respondents);
    setSuggestError(
      result.rejected.length
        ? `The company has denied access to ${result.rejected.join(', ')} — please contact them directly. Not added.`
        : result.invalid.length
          ? InvalidEmailsErrorMessage
          : null
    );
    setWorkingChips(result.chips);
    field.onChange(result.respondents);
  };

  const suggestProps = {
    value: chips,
    onChange: handleSuggestChange,
    onBlur: field.onBlur,
  } as unknown as ControllerRenderProps;

  return (
    <>
      <FormField>
        <Label description='Recipients already approved by this company. Read-only.'>
          Portco-verified respondents
        </Label>
        {approved.length ? (
          <Stack component='ul' sx={{ m: 0, mt: 0.5, p: 0, listStyle: 'none', gap: 0.5 }}>
            {approved.map((email) => (
              <Stack key={email} component='li' direction='row' spacing={0.75} alignItems='center'>
                <GppGoodIcon color='success' fontSize='small' />
                <Typography variant='body2' color='text.primary' sx={{ overflowWrap: 'anywhere' }}>
                  {email}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
            None yet
          </Typography>
        )}
      </FormField>
      <FormField>
        <Label description='The company will be notified of suggested respondent(s) and approve access at its discretion.'>
          Suggest respondents
        </Label>
        <FieldFreeLabels formField={suggestField} formProps={suggestProps} />
        <Collapse in={Boolean(suggestError)}>
          <Alert severity='error' sx={{ mt: '0.5rem' }}>
            {suggestError}
          </Alert>
        </Collapse>
      </FormField>
    </>
  );
}
